import { hideVisually, rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

import { StyledSharedHeader } from './Header.styles';
import { IStyledHeaderProps, IStyledNavChildLink } from './Header.types';

const StyledBox = css`
  background-color: ${({ theme }) => theme.colors.main};
  border-radius: ${rem('8px')};
  box-shadow: 0 0 ${rem('8px')} 0
    ${({ theme }) => rgba(theme.colors.cross.silverFox, 0.5)};
`;

export const StyledNavChildTitle = styled.span`
  display: flex;
  margin: ${rem('8px')} 0;
  padding: 0 ${rem('16px')};
  font-size: ${rem('16px')};
  font-weight: 400;
`;

export const StyledHeader = styled.header<IStyledHeaderProps>`
  ${StyledSharedHeader}
  display: flex;
  align-items: center;
  height: ${rem('72px')};
  background-color: ${({ theme }) => theme.colors.main};

  & > div {
    ${({ isFullWidth }) =>
      isFullWidth &&
      `
      margin: 0 ${rem('40px')};
      max-width: 100%;
    `};
  }

  ${StyledNavChildTitle} {
    color: ${({ theme, user }) => user && theme.colors[user].main};
  }

  @media screen and (max-width: calc(${({ theme }) =>
      theme.breakpoints.l} - 1px)) {
    display: none;
  }
`;

export const StyledLogoWrapper = styled.div`
  width: ${rem('110px')};
`;

export const StyledNavList = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
`;

export const StyledNavChildOuterWrapper = styled.div`
  padding-top: ${rem('16px')};

  &[aria-hidden='true'] {
    ${hideVisually()};
  }
`;

export const StyledNavChildInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem('8px')} 0;
  ${StyledBox}
  max-height: ${rem('282px')};
  overflow-y: auto;
`;

export const StyledNavListItem = styled.li`
  position: relative;
  list-style: none;

  &:not(:first-child) {
    margin-left: ${rem('16px')};
  }

  &:not(:last-child) {
    margin-right: ${rem('16px')};
  }

  ${StyledNavChildOuterWrapper} {
    position: absolute;
    top: ${rem('20px')};
    left: 0;
    width: ${rem('140px')};
    margin: 0;
  }
`;

export const StyledNavChildListItem = styled.li`
  list-style: none;

  ${StyledNavChildOuterWrapper} {
    top: 0;
    left: ${rem('140px')};
    width: ${rem('280px')};
    padding-left: ${rem('16px')};
    box-sizing: content-box;
  }
`;

export const StyledNavLink = styled.a`
  display: flex;
  justify-content: flex-end;
  color: ${({ color, theme }) => (color ? color : theme.colors.cross.grayson)};
  font-size: ${rem('14px')};
  font-weight: 700;
  line-height: 1.4;
  text-align: right;
  text-decoration: none;
  text-transform: uppercase;

  &:hover {
    color: ${({ theme }) => theme.colors.cross.black};
  }
`;

export const StyledPrimaryNav = styled.nav``;

export const StyledSecondaryNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;

  ${StyledNavListItem} {
    &:not(:first-child) {
      &::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: ${rem('-16px')};
        border-left: 1px solid ${({ theme }) => theme.colors.cross.silverFox};
        content: '';
      }
    }
  }
`;

export const StyledLanguageNav = styled.nav`
  position: relative;
  min-width: ${rem('50px')};
  color: ${({ theme }) => theme.colors.cross.grayson};
  font-size: ${rem('14px')};
  text-transform: uppercase;
`;

export const StyledLanguageNavContent = styled.div`
  position: absolute;
  top: ${rem('30px')};
  left: 0;
  padding-top: ${rem('10px')};

  &[aria-hidden='true'] {
    ${hideVisually()};
  }
`;

export const StyledLanguageNavList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${rem('24px')};
  ${StyledBox}
`;

export const StyledLanguageNavListItem = styled.li`
  list-style: none;
  font-weight: 400;

  &:not(:first-child) {
    margin-top: ${rem('16px')};
  }
`;

export const StyledNavChildLink = styled.a<IStyledNavChildLink>`
  display: flex;
  padding: ${rem('8px')} ${rem('16px')};
  color: ${({ activable, active, theme }) =>
    !activable
      ? theme.colors.cross.grayson
      : active
      ? theme.colors.cross.black
      : rgba(theme.colors.cross.grayson, 0.3)};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.cross.black};
  }
`;

export const StyledNavChildList = styled(StyledNavList)`
  flex-direction: column;
`;
